import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { CancelTokenSource } from 'axios';
import AddButton from 'components/AddButton';
import ConfirmActionButton from 'components/ConfirmActionButton';
import DatePicker from 'components/final-form/DatePicker';
import {
  DropdownOption,
  mapToDropdownOptionArray,
  stringArrayToDropdownOptionArray
} from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import Radio from 'components/final-form/Radio';
import { generateRadioButtonOptionsFromEnumAndPrefix } from 'components/final-form/radioUtils';
import Select from 'components/final-form/Select';
import InterventionAvailabilitiesSearchView
  from 'components/intervention-availability/InterventionAvailabilitiesSearchView';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxFormLabelSpan from 'components/layout/PraxFormLabelSpan';
import PxGrid from 'components/layout/PxGrid';
import { AnamnesisSeveritySelect } from 'components/patient-entry/AnamnesisSeveritySelect';
import { ANAMNESIS_SEVERITY_COLORS } from 'components/patient-entry/bookings-table/InterventionBookingsTableView';
import MSNTCodeActionButton from 'components/patient-entry/bookings-table/MSNTCodeActionButton';
import UpsertRandomMSNTGeneratorForm from 'components/patient-entry/bookings-table/UpsertRandomMSNTGeneratorForm';
import {
  InterventionBookings,
  InterventionBookingSearchRow,
  isBookingSelectable,
  isTestInterventionSelectable
} from 'components/patient-entry/PatientCentral';
import {
  PatientCentralConsumerRenderProps,
  withPatientCentralContext
} from 'components/patient-entry/PatientCentralContext';
import {
  PatientCentralRefDataConsumerRenderProps,
  withPatientCentralRefDataContext
} from 'components/patient-entry/PatientCentralRefDataContext';
import { FormState } from 'final-form';
import createDecorator from 'final-form-calculate';
import _ from 'lodash';
import React, { Component, FunctionComponent } from 'react';
import { Field, FieldRenderProps, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, useMessageSource, withMessages } from 'react-message-source';
import { RouteComponentProps } from 'react-router';
import {
  Button,
  ButtonGroup,
  Dropdown,
  DropdownItemProps,
  DropdownProps,
  Grid,
  Icon,
  Loader,
  Modal
} from 'semantic-ui-react';
import { SemanticWIDTHS } from 'semantic-ui-react/dist/commonjs/generic';
import { getAllActiveDivisionsByCompany } from 'services/divisionServices';
import axios from 'services/http';
import { getAllMandatesWithDeleted } from 'services/mandateServices';
import styled from 'styled-components';
import { debounce } from 'ts-debounce';
import {
  AnamnesisSeverity,
  InterventionBookingInputChannel,
  InterventionBookingSearchBy,
  InterventionBookingStatus,
  InterventionBookingTestResult,
  PoolSearchFilter,
  Privilege,
  TestDateSearchBy,
  Urgency
} from 'ts-types/api.enums';
import { DivisionDto, InterventionTestBookingSearchResult } from 'ts-types/api.types';
import { applyStyles } from 'utils/textUtils';
import { withRouterWorkaround } from 'utils/workaroundUtils';


const StyledForm = styled.form`
  & .ui.grid > .row {
    padding-top: 0.2rem !important;
    padding-bottom: 0.5rem !important;
  }

  & .ui.grid {
    margin-top: 0.7rem;
    margin-bottom: 0.5rem;
  }

  .from-to-date-input {
    display: flex;
    flex-direction: row;
    width: 100%;

    .to-label {
      flex: 0 0 auto;
      align-self: center;
      padding-left: 1rem;
      padding-right: 0.5rem;
    }

    .date-input {
      flex: 1 1 auto;
    }

  }

  .from-to-date-age-input {
    display: flex;
    flex-direction: row;
    width: 100%;

    .to-label {
      flex: 0 0 auto;
      align-self: center;
      padding-left: 1rem;
      padding-right: 0.5rem;
    }

    .field {
      flex: 1 0 auto;
    }
  }

  .radio-container .field {
    display: inline-block;
    margin-right: 1.2rem;
  }

  .trigger-wrapper {
    display: inline-block;
  }

  .results-summary {
    display: inline-block;
    margin-left: 1rem;
    color: #575757;

    .highlight-value {
      color: rgb(48 145 218);
      font-weight: bold;
    }
  }

  .vaccination-status-select {
    display: inline-block;
    margin-left: 1rem;
  }

  .test-verify-select {
    display: inline-block;
    margin-left: 1rem;
  }

  .ui.inline.loader {
    margin-left: .6rem;
  }

  .not-applicable {
    color: #757575;
  }
`;

const NewPatientDiv = styled.div`
  display: block;
  margin: 5px 0;
`;

interface FromRowProps {
  fieldName: string;
  labelKey: string;
  valueClassName?: string;
  widthCol1?: SemanticWIDTHS;
  widthCol2?: SemanticWIDTHS;
  widthCol3?: SemanticWIDTHS;
}

const FormRow: React.FunctionComponent<FromRowProps> = (props) => {

  const {getMessage} = useMessageSource();

  const {
    widthCol1 = 6,
    widthCol2 = 10,
    valueClassName
  } = props;

  return <Grid.Row>
    <Grid.Column width={widthCol1} verticalAlign="middle">
      <PraxFormLabelSpan htmlFor={props.fieldName}>{getMessage(props.labelKey)}</PraxFormLabelSpan>
    </Grid.Column>
    <Grid.Column width={widthCol2} verticalAlign="middle" className={`${valueClassName || ""}`}>
      {props.children}
    </Grid.Column>
  </Grid.Row>;
}

interface Props extends RouteComponentProps<any>,
    MessageSourceProps,
    AuthConsumerRenderProps,
    PatientCentralRefDataConsumerRenderProps,
    PatientCentralConsumerRenderProps {

  onOnboardPatient: () => void,
  onFilterBookings: (bookings: InterventionBookings) => void,
  sendVaccinationInvitations: () => void,
  verifyTestResults: () => void,
  bookings: InterventionBookings,
  selectedInterventionBookings: Set<number>,
  selectedTestInterventionBookings: Set<number>,
  onChangeSelectVaccinationStatus: (value: any) => void,
  onChangeSelectTestVerification: (value: any) => void
}

export interface InterventionBookingSearchFilter {
  patientQuery: string;
  searchBy: InterventionBookingSearchBy;
  testDateSearchBy: TestDateSearchBy;
  vaccinationDateSearchBy: TestDateSearchBy,
  inputChannel: InterventionBookingInputChannel | "any";
  entryFromDate: string;
  entryToDate: string;
  entryFromAge: number;
  entryToAge: number;
  questionSetId: number;
  anamnesisSeverity: string;
  interventionId: number;
  locationId: number;
  mandateId: number;
  productId: string;
  interventionFromDate: string;
  interventionToDate: string;
  companyId?: number;
  divisionId?: number;
  testKitCode?: string;
  exportToFile?: boolean;
  status: InterventionBookingStatus | "any";
  resultVerified: string;
  resultControlled: string;
  testResult?: string;
  poolFilter?: PoolSearchFilter;
  urgency?: Urgency;
}

interface State {
  companyOptions: DropdownOption[];
  divisionOptions: DropdownOption[];
  urgencyOptions: DropdownOption[];
  productOptions: DropdownOption[];
  searchOptions: DropdownOption[];
  vaccinationSearchOptions: DropdownOption[];
  superMandateSearchOptions: DropdownOption[];
  searchAvailabilitiesModalOpen: boolean;
  cancelTokenSource: CancelTokenSource;
  errorMessage?: String;
  msntCodeSetModalOpen: boolean;
  msntCode: string;
}

class InterventionBookingsSearchView extends Component<Props, State> {

  cancelTokenSource: CancelTokenSource = axios.CancelToken.source();

  initialFormValues: Partial<InterventionBookingSearchFilter> = {};

  formDecorators = [
    createDecorator({
      field: "companyId",
      updates: {
        divisionId: (companyValue: any) => ""
      }
    }),

    createDecorator({
      field: "searchBy",
      updates: {
        interventionId: (fieldValue, allValues: any) => {

          const {
            interventions
          } = this.props;

          let interventionId = allValues.interventionId;

          let activeInterventions = interventions
              .filter(intervention => intervention.active);

          if (fieldValue === InterventionBookingSearchBy.COVID_TEST) {
            let interventionTestIds = activeInterventions
                .filter(inv => inv.type === "COVID_TEST")
                .map(intervention => intervention.id);

            if (interventionTestIds.includes(interventionId)) {
              return interventionId;
            }
            return undefined;
          }

          if (fieldValue === InterventionBookingSearchBy.VACCINATION) {
            let interventionVaccinationIds = activeInterventions
                .filter(inv => fieldValue ? inv.type === "COVID_VACCINATION" : true)
                .map(intervention => intervention.id);

            if (interventionVaccinationIds.includes(interventionId)) {
              return interventionId;
            }
            return -1;
          }

          return interventionId;

        }
      }
    })
  ];

  constructor(props: Props) {
    super(props);

    this.initialFormValues = props.bookingSearchRequest;

    const {currentUser, products, getMessage} = props;

    const cancelTokenSource = axios.CancelToken.source();

    const companyOptions = mapToDropdownOptionArray(this.props.companies, "description");

    const searchOptions = stringArrayToDropdownOptionArray(Object.values(TestDateSearchBy), props.getMessage, "enum.testDateSearchBy");

    const vaccinationSearchOptions = searchOptions.filter(option =>
        option.value === TestDateSearchBy.BOOKING_DATE
        || option.value === TestDateSearchBy.APPOINTMENT_DATE
        || option.value === TestDateSearchBy.CHECK_IN_DATE
    )

    const urgencyOptions = stringArrayToDropdownOptionArray(
        Object.values(Urgency), props.getMessage, "enum.urgency");


    let productOptions: DropdownOption[] = [
      {
        key: "all",
        text: getMessage("patientCentral.overview.search.allProducts"),
        value: "all"
      }
    ];

    products.forEach(product => {
      const defaultDesc = !_.isEmpty(product.description) ? product.description : undefined;

      const texts: { [key: string]: string | undefined } = {
        "de": defaultDesc,
        "en": !_.isEmpty(product.descriptionEn) ? product.descriptionEn : defaultDesc,
        "fr": !_.isEmpty(product.descriptionFr) ? product.descriptionFr : defaultDesc,
        "it": !_.isEmpty(product.descriptionIt) ? product.descriptionIt : defaultDesc
      };

      const lang = currentUser ? currentUser.userConfig.language : "de";
      const desc = texts[lang] ? ` - ${texts[lang]}` : "";

      productOptions.push({
        key: product.id,
        text: `${product.id} ${desc}`,
        value: product.id
      })
    });

    this.state = {
      companyOptions,
      searchOptions,
      vaccinationSearchOptions,
      superMandateSearchOptions: [],
      divisionOptions: [],
      urgencyOptions: urgencyOptions,
      productOptions: productOptions,
      searchAvailabilitiesModalOpen: false,
      cancelTokenSource,
      msntCodeSetModalOpen: false,
      msntCode: ""
    }

    this.fetchMandates();
  }

  handleChange = (values: Partial<InterventionBookingSearchFilter>) => {
    this.triggerSearchBookings();
  };

  fetchMandates = async () => {

    const {cancelTokenSource} = this.state;

    const allMandates = await getAllMandatesWithDeleted(cancelTokenSource);

    let superMandateSearchOptions = mapToDropdownOptionArray(allMandates, "name");

    this.setState({
      superMandateSearchOptions
    });
  }

  onFormUpdate = (formState: FormState) => {

    const values = formState.values as Partial<InterventionBookingSearchFilter>;

    const prevFilter = this.props.bookingSearchRequest;
    const companyId = values.companyId;
    const companyChanged = prevFilter.companyId !== companyId;
    if (companyChanged) {
      this.fetchDivisions(!_.isNil(companyId) ? `${companyId}` : undefined);
    }

    this.props.setBookingSearchRequest(values);
    this.triggerSearchBookings();
  }

  fetchDivisions = async (companyId?: string) => {

    let divisions: DivisionDto[] = [];
    try {
      if (companyId) {
        divisions = await getAllActiveDivisionsByCompany(companyId, this.state.cancelTokenSource);
      }
    } catch (e) {
      // do nothing
    }
    const divisionOptions = mapToDropdownOptionArray(divisions, "name")

    this.setState({
      divisionOptions: divisionOptions
    });
  };

  triggerSearchBookings = debounce(() => {
    this.props.searchBookings(this.props.onFilterBookings);
  }, 350);

  onVaccinationStatusDropdownChange = (e: any, {value}: DropdownProps) => {
    this.props.onChangeSelectVaccinationStatus(value);
  }

  onTestVerificationDropdownChange = (e: any, {value}: DropdownProps) => {
    this.props.onChangeSelectTestVerification(value);
  }

  openSearchAvailabilitiesModal = () => {
    this.setState({
      searchAvailabilitiesModalOpen: true
    });
  }

  hideSearchAvailabilitiesModal = () => {
    this.setState({
      searchAvailabilitiesModalOpen: false
    });
  };

  showSetRandomMSNTCodeModal = (msntCode: string) => {
    this.setState(() => ({
      msntCodeSetModalOpen: true,
      msntCode: msntCode
    }));
  };

  hideSetRandomMSNTCodeModal = () => {
    this.setState({
      msntCodeSetModalOpen: false
    });
  };

  setErrorMessage = (error?: String) => {
    this.setState({
      errorMessage: error
    })
  }

  render(): React.ReactNode {
    return (
        <React.Fragment>
          <FinalForm
              onSubmit={(values, form) => {
                this.handleChange(values);
              }}
              initialValues={this.initialFormValues}
              decorators={this.formDecorators}
              render={this.renderForm}
          />
          {this.renderUpsertInterventionAvailabilitySearchModal()}
          {this.renderUpsertMSNTCodeModal()}
        </React.Fragment>
    );
  }

  renderForm = ({form, handleSubmit, values}: FormRenderProps): React.ReactNode => {

    const {selectVaccinationStatus, selectVerifyTestResults, language} = this.props;

    const {
      bookings,
      selectedInterventionBookings,
      selectedTestInterventionBookings,
      batchTestVerificationEnabled,
      onOnboardPatient,
      interventions,
      resources,
      questionSets,
      bookingsLoading,
      currentUser,
      getMessage,
      currentUserHasPrivilege
    } = this.props;

    const {searchOptions, vaccinationSearchOptions, superMandateSearchOptions} = this.state;

    const isSuperMandate = currentUser!.userConfig.modifyPatientsInOtherMandates;

    const searchBy = values.searchBy;
    const entrySearch = searchBy === InterventionBookingSearchBy.ENTRY_DATE;
    const appointmentSearch = searchBy === InterventionBookingSearchBy.APPOINTMENT_DATE;
    const testSearch = searchBy === InterventionBookingSearchBy.COVID_TEST;
    const vaccinationSearch = searchBy === InterventionBookingSearchBy.VACCINATION;

    const interventionBookingSearchByOptions = Object.values(InterventionBookingSearchBy);

    const inputChannelOptions = Object.values(InterventionBookingInputChannel).map(inputChannel => {
      return {
        key: inputChannel,
        text: getMessage(`enum.interventionBookingInputChannel.${inputChannel}`),
        value: inputChannel
      }
    });
    const allText = getMessage("patientCentral.overview.search.any");
    inputChannelOptions.unshift({key: "any", text: allText, value: "any"});

    const statusOptions = Object.values(InterventionBookingStatus).map(bookingStatus => {
      return {
        key: bookingStatus,
        text: getMessage(`enum.interventionBookingStatus.${bookingStatus}`),
        value: bookingStatus
      }
    });
    statusOptions.unshift({key: "any", text: allText, value: "any"});

    const testResults = Object.values(InterventionBookingTestResult);
    let testResultOptions = stringArrayToDropdownOptionArray(testResults, getMessage, "enum.testResult");
    testResultOptions.push({
      key: "WITHOUT_RESULT",
      value: "WITHOUT_RESULT",
      text: getMessage("patientCentral.overview.search.withoutResult")
    });

    const poolFilterOptions =
        stringArrayToDropdownOptionArray(Object.values(PoolSearchFilter), getMessage, "enum.poolSearchFilter");

    const resultVerifiedRadioDefinition = generateRadioButtonOptionsFromEnumAndPrefix(
        ["yes", "no", "both"], "resultVerified", "enum.resultVerifiedOption", getMessage);

    const resultControlledRadioDefinition = generateRadioButtonOptionsFromEnumAndPrefix(
        ["yes", "no", "both"], "resultControlled", "enum.resultControlledOption", getMessage);

    // const bookingInterventions = new Set(bookings.map(b => b.interventionId));

    let activeInterventions = interventions
        .filter(intervention => intervention.active);

    let interventionOptions = activeInterventions
        .filter(inv => vaccinationSearch ? inv.type === "COVID_VACCINATION" : true)
        .map(intervention => {
          return {
            key: intervention.id,
            text: intervention.description,
            value: intervention.id
          }
        });
    if (vaccinationSearch || appointmentSearch) {
      interventionOptions.unshift({key: -1, text: allText, value: -1});
    }

    let interventionOptionsTest = activeInterventions
        .filter(inv => inv.type === "COVID_TEST")
        .map(intervention => {
          return {
            key: intervention.id,
            text: intervention.description,
            value: intervention.id
          }
        });

    const questionSetOptions = questionSets.map(qs => {
      const nameMap: { [lang: string]: string } =
          {
            "de": qs.name,
            "en": qs.nameEn || qs.name,
            "fr": qs.nameFr || qs.name,
            "it": qs.nameIt || qs.name
          };
      return {
        key: qs.id,
        text: nameMap[language],
        value: qs.id
      }
    });
    questionSetOptions.unshift({key: -1, text: allText, value: -1});

    const anamnesisSeverityOptions: DropdownItemProps[] = Object.values(AnamnesisSeverity).map(severity => {
      return {
        key: severity,
        text: getMessage(`enum.AnamnesisSeverity.${severity}`),
        value: severity,
        label: {color: ANAMNESIS_SEVERITY_COLORS[severity], empty: true, circular: true}
      }
    });
    anamnesisSeverityOptions.unshift({
      key: "any",
      text: allText,
      value: "any",
      icon: {name: "circle outline", color: "grey"}
    });

    const resourceOptions = resources.map(res => {
      return {
        key: res.id,
        text: res.description,
        value: res.id
      }
    });
    resourceOptions.unshift({key: -1, text: allText, value: -1});

    const vaccinationsStatusOptions = [
      {
        key: InterventionBookingStatus.REQUESTED,
        text: getMessage(`patientCentral.overview.${InterventionBookingStatus.REQUESTED}.select`),
        value: InterventionBookingStatus.REQUESTED
      },
      {
        key: InterventionBookingStatus.ON_WAIT_LIST,
        text: getMessage(`patientCentral.overview.${InterventionBookingStatus.ON_WAIT_LIST}.select`),
        value: InterventionBookingStatus.ON_WAIT_LIST
      }
    ];

    const testVerifyOptions = [
      {
        key: "enabledSelection",
        text: getMessage("patientCentral.overview.testVerify.enable.select"),
        value: true
      },
      {
        key: "disabledSelection",
        text: getMessage("patientCentral.overview.testVerify.disable.select"),
        value: false
      }
    ]

    const numResults = bookings.length;
    const numSelectable = (bookings as InterventionBookingSearchRow[])
        .filter(booking => isBookingSelectable(booking, selectVaccinationStatus))
        .length;
    const numSelected = selectedInterventionBookings.size;

    const numSelectableTests = (bookings as InterventionTestBookingSearchResult[])
        .filter(booking => isTestInterventionSelectable(booking, selectVerifyTestResults))
        .length;
    const numSelectedTests = selectedTestInterventionBookings.size;

    const disableSearchBtn = bookingsLoading;

    const showMsntButton = currentUserHasPrivilege(Privilege.PX_CORE_GENERATE_RANDOM_MSNT_CODE);

    return (
        <StyledForm
            onSubmit={handleSubmit}
        >
          <FormContainer>
            <FormContent>
              <PxGrid>
                <Grid.Row>
                  <Grid.Column width={2} verticalAlign="middle">
                    <PraxFormLabelSpan htmlFor="tariffId">
                      {getMessage('patientCentral.overview.search.patient')}
                    </PraxFormLabelSpan>
                  </Grid.Column>
                  <Grid.Column width={5} verticalAlign="middle">
                    <Field
                        name="patientQuery"
                        component={Input}
                        fluid
                    />
                  </Grid.Column>
                  <Grid.Column width={9} textAlign="right">
                    <NewPatientDiv>
                      <AddButton
                          type="button"
                          buttonMessage={getMessage("patientCentral.home.onboardPatient")}
                          hideIcon
                          onClick={() => onOnboardPatient()}
                      />

                      <Button
                          type="button"
                          primary
                          compact
                          floated="right"
                          onClick={() => this.openSearchAvailabilitiesModal()}
                      >
                        {getMessage("interventionAvailability.form.searchAvailabilities")}
                      </Button>

                      {
                          batchTestVerificationEnabled && testSearch &&
                          <ConfirmActionButton
                              popupMessage={getMessage("patientCentral.overview.sendTestsForVerification.confirmMessage")}
                              popupPosition="bottom center"
                              trigger={
                                <Button
                                    type="button"
                                    compact
                                    disabled={selectedTestInterventionBookings.size === 0}
                                >
                                  {getMessage("patientCentral.overview.sendTestsForVerification.button")}

                                </Button>}
                              onConfirm={() => {
                                this.props.verifyTestResults()
                              }}/>
                      }
                      {
                          vaccinationSearch &&
                          <ConfirmActionButton
                              popupMessage={selectVaccinationStatus === InterventionBookingStatus.REQUESTED
                                  ? getMessage("patientCentral.overview.sendCovidVaccinationSlotsEnabled.confirmMessage")
                                  : getMessage("patientCentral.overview.remindParticipantsForAvailableSlots.confirmMessage")
                              }
                              popupPosition="bottom center"
                              trigger={
                                <Button
                                    type="button"
                                    compact
                                    disabled={selectedInterventionBookings.size === 0}
                                >
                                  {selectVaccinationStatus === InterventionBookingStatus.REQUESTED
                                      ? getMessage("patientCentral.overview.sendCovidVaccinationSlotsEnabled.button")
                                      : getMessage("patientCentral.overview.remindParticipantsForAvailableSlots.button")
                                  }
                                </Button>}
                              onConfirm={() => {
                                this.props.sendVaccinationInvitations()
                              }}/>
                      }

                      {
                          showMsntButton &&
                          <MSNTCodeActionButton
                              hoverMessage={getMessage("patientCentral.overview.actions.setRandomMSNTCode")}
                              trigger={<Icon name={'barcode'}/>}
                              onConfirm={() => this.showSetRandomMSNTCodeModal(this.state.msntCode)}
                          />
                      }

                      <Button
                          type="button"
                          compact
                          disabled={bookings.length === 0}
                          onClick={() => this.props.searchBookings(undefined, true)}
                      >
                        {getMessage("interventionAvailability.export")}
                      </Button>

                    </NewPatientDiv>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={2} verticalAlign="middle">
                    <PraxFormLabelSpan htmlFor="tariffId">{getMessage('searchOptions')}</PraxFormLabelSpan>
                  </Grid.Column>
                  <Grid.Column width={5} verticalAlign="middle">
                    <ButtonGroup compact size="small">
                      {interventionBookingSearchByOptions.map(filter => (
                          <Button key={filter}
                                  type="button"
                                  active={searchBy === filter}
                                  onClick={() => form.change("searchBy", filter)}>
                            {getMessage(`enum.interventionBookingSearchBy.${filter}`)}
                          </Button>
                      ))}
                    </ButtonGroup>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={6}>
                    <PxGrid>
                      <FormRow fieldName="status" labelKey="patientCentral.overview.tableHeader.status">
                        <Field
                            name="status"
                            component={Select}
                            fluid
                            search={true}
                            options={statusOptions}
                        />
                      </FormRow>

                      {
                          !testSearch &&
                          <FormRow fieldName="questionSetId" labelKey="patientCentral.overview.tableHeader.anamnesis">
                            <Field
                                name="questionSetId"
                                component={Select}
                                fluid
                                search={true}
                                options={questionSetOptions}
                            />
                          </FormRow>
                      }

                      {
                          !testSearch && !vaccinationSearch &&
                          <FormRow
                              fieldName="anamnesisSeverity"
                              labelKey="patientCentral.overview.tableHeader.anamnesisSeverity"
                              valueClassName="severity-container"
                          >
                            <Field
                                name="anamnesisSeverity"
                                component={AnamnesisSeveritySelect as unknown as FunctionComponent<FieldRenderProps>}
                                fluid
                                search={true}
                                options={anamnesisSeverityOptions}
                            />
                          </FormRow>
                      }

                      {
                          vaccinationSearch &&
                          <FormRow fieldName="interventionId" labelKey="patientCentral.overview.search.intervention">
                            <Field
                                name="interventionId"
                                component={Select}
                                fluid
                                search={true}
                                options={interventionOptions}
                            />
                          </FormRow>
                      }

                      {
                          testSearch &&
                          <FormRow
                              fieldName="companyId"
                              labelKey="patientCentral.overview.tableHeader.company"
                          >
                            <Field
                                id="companyId"
                                name="companyId"
                                component={Select}
                                fluid
                                search={true}
                                clearable
                                options={this.state.companyOptions}
                            />
                          </FormRow>
                      }

                      {
                          testSearch &&
                          <FormRow
                              fieldName="divisionId"
                              labelKey="patientCentral.overview.tableHeader.division"
                          >
                            <Field
                                id="divisionId"
                                name="divisionId"
                                component={Select}
                                fluid
                                search={true}
                                clearable
                                disabled={!values.companyId}
                                options={this.state.divisionOptions}
                            />
                          </FormRow>
                      }

                      {
                          (entrySearch || testSearch || vaccinationSearch) &&
                          <>
                            <FormRow fieldName="inputChannel" labelKey="patientCentral.overview.search.inputChannel">
                              <Field
                                  name="inputChannel"
                                  component={Select}
                                  fluid
                                  search={true}
                                  options={inputChannelOptions}
                              />
                            </FormRow>
                          </>
                      }

                      {
                          (vaccinationSearch) &&
                          <>
                            <FormRow
                                fieldName="entryFromAge"
                                labelKey="patientCentral.overview.search.entryAgeFrom"
                                widthCol3={4}
                            >
                              <div className="from-to-date-age-input">
                                <Field
                                    name="entryFromAge"
                                    component={Input}
                                    className="age-input"
                                    fluid
                                />
                                <PraxFormLabelSpan className="to-label" htmlFor="entryToAge">
                                  {getMessage('patientCentral.overview.search.entryAgeTo')}
                                </PraxFormLabelSpan>
                                <Field
                                    name="entryToAge"
                                    component={Input}
                                    className="age-input"
                                    fluid
                                />
                              </div>
                            </FormRow>
                          </>
                      }

                      {
                          (entrySearch || testSearch || vaccinationSearch) &&
                          <>
                            {testSearch &&
                                <FormRow fieldName="testDateSearchBy"
                                         labelKey="patientCentral.overview.search.testDateSearchBy"
                                         widthCol3={4}
                                >
                                  {
                                    <Field
                                        name="testDateSearchBy"
                                        component={Select}
                                        fluid
                                        options={searchOptions}
                                    />
                                  }
                                </FormRow>
                            }
                            {vaccinationSearch &&
                                <FormRow fieldName="vaccinationDateSearchBy"
                                         labelKey="patientCentral.overview.search.testDateSearchBy"
                                         widthCol3={4}
                                >
                                  {
                                    <Field
                                        name="vaccinationDateSearchBy"
                                        component={Select}
                                        fluid
                                        options={vaccinationSearchOptions}
                                    />
                                  }
                                </FormRow>
                            }
                            <FormRow
                                fieldName="entryFromDate"
                                labelKey={testSearch ? "from" : "patientCentral.overview.search.entryDateFrom"}
                                widthCol3={4}
                            >
                              <div className="from-to-date-input">
                                <Field
                                    name="entryFromDate"
                                    component={DatePicker}
                                    emptyDateInput
                                    fluid
                                />
                                <PraxFormLabelSpan className="to-label" htmlFor="entryToDate">
                                  {testSearch
                                      ? getMessage('to')
                                      : getMessage('patientCentral.overview.search.entryDateTo')
                                  }
                                </PraxFormLabelSpan>
                                <Field
                                    name="entryToDate"
                                    component={DatePicker}
                                    emptyDateInput
                                    fluid
                                />
                              </div>
                            </FormRow>
                            {isSuperMandate &&
                                <FormRow fieldName="superMandate"
                                         labelKey="patientCentral.overview.search.superMandate"
                                         widthCol3={4}
                                >
                                  <Field
                                      id="mandateId"
                                      name="mandateId"
                                      component={Select}
                                      fluid
                                      clearable
                                      search={true}
                                      options={superMandateSearchOptions}
                                  />
                                </FormRow>

                            }
                          </>
                      }

                      {
                          appointmentSearch &&
                          <>
                            <FormRow fieldName="interventionId" labelKey="patientCentral.overview.search.intervention">
                              <Field
                                  name="interventionId"
                                  component={Select}
                                  fluid
                                  search={true}
                                  options={interventionOptions}
                              />
                            </FormRow>

                            <FormRow fieldName="locationId" labelKey="patientCentral.overview.search.location">
                              <Field
                                  name="locationId"
                                  component={Select}
                                  fluid
                                  search={true}
                                  options={resourceOptions}
                              />
                            </FormRow>

                            <FormRow
                                fieldName="interventionFromDate"
                                labelKey="patientCentral.overview.search.interventionDateFrom"
                            >
                              <div className="from-to-date-input">
                                <Field
                                    name="interventionFromDate"
                                    component={DatePicker}
                                    emptyDateInput
                                    fluid
                                />
                                <PraxFormLabelSpan className="to-label" htmlFor="tariffId">
                                  {getMessage('patientCentral.overview.search.entryDateTo')}
                                </PraxFormLabelSpan>
                                <Field
                                    name="interventionToDate"
                                    component={DatePicker}
                                    emptyDateInput
                                    fluid
                                />
                              </div>
                            </FormRow>
                            {isSuperMandate &&
                                <FormRow fieldName="superMandate"
                                         labelKey="patientCentral.overview.search.superMandate"
                                         widthCol3={4}
                                >
                                  <Field
                                      id="mandateId"
                                      name="mandateId"
                                      component={Select}
                                      fluid
                                      clearable
                                      search={true}
                                      options={superMandateSearchOptions}
                                  />
                                </FormRow>

                            }
                          </>
                      }
                    </PxGrid>
                  </Grid.Column>

                  <Grid.Column width={6}>
                    <PxGrid>
                      {
                          testSearch &&
                          <React.Fragment>
                            <FormRow
                                fieldName="testKitCode"
                                labelKey="patientCentral.overview.tableHeader.testKitCode"
                            >
                              <Field
                                  id="testKitCode"
                                  name="testKitCode"
                                  component={Input}
                                  fluid
                              />
                            </FormRow>

                            <FormRow fieldName="testResult"
                                     labelKey="patientCentral.overview.search.testResult"
                            >
                              <Field
                                  name="testResult"
                                  component={Select}
                                  fluid
                                  search={true}
                                  clearable
                                  openOnFocus={false}
                                  options={testResultOptions}
                              />
                            </FormRow>

                            <FormRow fieldName="resultVerified"
                                     labelKey="patientCentral.overview.search.resultVerified"
                                     valueClassName="radio-container"
                            >
                              {
                                resultVerifiedRadioDefinition.map((radioDef, ix) => {
                                  return <Field
                                      key={`result-radio-${ix}`}
                                      name="resultVerified"
                                      component={Radio}
                                      radioDefinition={radioDef}
                                  />;
                                })
                              }
                            </FormRow>

                            <FormRow fieldName="resultControlled"
                                     labelKey="patientCentral.overview.search.resultControlled"
                                     valueClassName="radio-container"
                            >
                              {
                                values.resultVerified !== "no"
                                    ? <>
                                      {
                                        resultControlledRadioDefinition.map((radioDef, ix) => {
                                          return <Field
                                              key={`result-radio-${ix}`}
                                              name="resultControlled"
                                              component={Radio}
                                              radioDefinition={radioDef}
                                          />;
                                        })
                                      }
                                    </>
                                    : <span className="not-applicable">n/a</span>
                              }
                            </FormRow>

                            <FormRow
                                fieldName="poolFilter"
                                labelKey="patientCentral.overview.search.poolFilter"
                            >
                              <Field
                                  id="poolFilter"
                                  name="poolFilter"
                                  component={Select}
                                  fluid
                                  search={true}
                                  clearable
                                  options={poolFilterOptions}
                              />
                            </FormRow>

                            <FormRow fieldName="interventionId" labelKey="patientCentral.overview.search.intervention">
                              <Field
                                  name="interventionId"
                                  component={Select}
                                  fluid
                                  search={true}
                                  clearable
                                  options={interventionOptionsTest}
                              />
                            </FormRow>

                            <FormRow
                                fieldName="locationId"
                                labelKey="patientCentral.overview.search.location"
                            >
                              <Field
                                  name="locationId"
                                  component={Select}
                                  fluid
                                  search={true}
                                  clearable
                                  options={resourceOptions}
                              />
                            </FormRow>
                            <FormRow
                                fieldName="urgency"
                                labelKey="patientCentral.overview.search.urgency"
                            >
                              <Field
                                  name="urgency"
                                  component={Select}
                                  fluid
                                  search={true}
                                  clearable
                                  options={this.state.urgencyOptions}
                              />
                            </FormRow>
                            <FormRow
                                fieldName="productId"
                                labelKey="patientCentral.overview.search.productId"
                            >
                              <Field
                                  name="productId"
                                  component={Select}
                                  fluid
                                  search={true}
                                  clearable
                                  options={this.state.productOptions}
                              />
                            </FormRow>
                          </React.Fragment>
                      }
                    </PxGrid>
                  </Grid.Column>
                </Grid.Row>

                <Grid.Row>
                  <Grid.Column width={16} verticalAlign="bottom">
                    <Button type="button" primary size="tiny" compact
                            onClick={evt => handleSubmit()}
                            disabled={disableSearchBtn}
                    >
                      {getMessage("search")}
                      {bookingsLoading && <Loader active inverted inline size="mini"/>}
                    </Button>

                    {
                        batchTestVerificationEnabled && testSearch && numResults > 0 &&
                        <React.Fragment>
                          <div className={"results-summary"}>
                            {
                              applyStyles(
                                  getMessage("patientCentral.overview.search.resultsSummaryWithSelected")
                                      .replace("{{numResults}}", "" + numResults)
                                      .replace("{{numSelectable}}", "" + numSelectableTests)
                                      .replace("{{numSelected}}", "" + numSelectedTests)
                              )
                            }
                          </div>
                          <div className={"test-verify-select"}>
                            <Dropdown
                                inline
                                value={this.props.selectVerifyTestResults}
                                options={testVerifyOptions}
                                onChange={this.onTestVerificationDropdownChange}
                            />
                          </div>


                        </React.Fragment>

                    }

                    {
                        vaccinationSearch && numResults > 0 &&
                        <React.Fragment>
                          <div className={"results-summary"}>
                            {
                              applyStyles(
                                  getMessage("patientCentral.overview.search.resultsSummaryWithSelected")
                                      .replace("{{numResults}}", "" + numResults)
                                      .replace("{{numSelectable}}", "" + numSelectable)
                                      .replace("{{numSelected}}", "" + numSelected)
                              )
                            }
                          </div>
                          <div className={"vaccination-status-select"}>
                            <Dropdown
                                inline
                                value={this.props.selectVaccinationStatus}
                                options={vaccinationsStatusOptions}
                                onChange={this.onVaccinationStatusDropdownChange}
                            />
                          </div>
                        </React.Fragment>
                    }

                    {
                        !vaccinationSearch
                        && !(batchTestVerificationEnabled && testSearch)
                        && numResults > 0 &&
                        <div className={"results-summary"}>
                          {
                            applyStyles(
                                getMessage("search.resultsSummary")
                                    .replace("{{numResults}}", "" + numResults)
                            )
                          }
                        </div>
                    }
                  </Grid.Column>
                </Grid.Row>
              </PxGrid>
            </FormContent>
            <FormSpy subscription={{values: true}} onChange={this.onFormUpdate}/>
          </FormContainer>
        </StyledForm>
    )
  }

  renderUpsertInterventionAvailabilitySearchModal = (): React.ReactNode => {

    const {interventions, resources, getMessage} = this.props;
    const {searchAvailabilitiesModalOpen} = this.state;

    return (
        <Modal
            open={searchAvailabilitiesModalOpen}
            onClose={() => this.hideSearchAvailabilitiesModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("header.navigation.availabilities")}</Modal.Header>
          <FormModalContentContainer>
            <InterventionAvailabilitiesSearchView
                interventions={interventions}
                resources={resources}
                onCancel={this.hideSearchAvailabilitiesModal}
            />
          </FormModalContentContainer>
        </Modal>
    );
  };

  renderUpsertMSNTCodeModal = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {msntCodeSetModalOpen, msntCode} = this.state;

    return (
        <Modal
            open={msntCodeSetModalOpen}
            onClose={() => this.hideSetRandomMSNTCodeModal()}
            closeOnDimmerClick={false}
            style={{width: "700px"}}
        >
          <Modal.Header>{getMessage("header.navigation.msntCode")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertRandomMSNTGeneratorForm
                msntCode={msntCode}
                onCancel={this.hideSetRandomMSNTCodeModal}
            />
          </FormModalContentContainer>
        </Modal>
    );
  };


}

export default withRouterWorkaround(
    withMessages(
        withAuthContext(
            withPatientCentralRefDataContext(
                withPatientCentralContext(
                    InterventionBookingsSearchView)))));
